import type { RequestFn } from '../http';

/**
 * Kundenanlage-Vorschläge: Techniker schlägt einen neuen Kunden vor, Admins geben
 * frei oder lehnen ab. Die Freigabe legt den Kunden im ServiceHub an (Kundennummer
 * vergibt der ServiceHub) und schickt ihn danach nach weclapp. Offene Vorschläge werden
 * stündlich gegen den Kundenstamm gehalten — existiert der Kunde (Name/Adresse) schon,
 * gilt der Vorschlag als erledigt.
 */

export interface CustomerCreationRequestInput {
  companyName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  website?: string;
  notes?: string;
}

export interface CustomerDuplicateMatchDto {
  /** null = steht im ServiceHub, weclapp hat ihn noch nicht. */
  weclappPartyId: string | null;
  customerNumber: string | null;
  localCustomerId: string | null;
  name: string;
  street: string | null;
  zipCode: string | null;
  city: string | null;
  confidence: 'high' | 'medium';
  matchedOn: Array<'name' | 'address'>;
  detail: string;
}

/** Was der Techniker schon weiß — mindestens der Name. */
export interface CustomerProfileResearchHints {
  companyName: string;
  city?: string;
  email?: string;
  website?: string;
  phone?: string;
  contactName?: string;
}

export interface CustomerProfileCandidateDto {
  companyName: string;
  contactName: string | null;
  /** „Inhaber", „Geschäftsführer" … laut Impressum. */
  contactRole: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  zipCode: string | null;
  city: string | null;
  description: string | null;
  sources: string[];
  /** Diesen Kunden gibt es schon. */
  duplicate: CustomerDuplicateMatchDto | null;
}

export interface CustomerProfileResearchResult {
  /** Gibt es überhaupt einen such-fähigen Anbieter? */
  available: boolean;
  /** Plausibelster zuerst, höchstens drei. */
  candidates: CustomerProfileCandidateDto[];
  confidence: 'high' | 'medium' | 'low' | null;
  remark: string;
  /** Der eingetippte Name existiert schon. */
  duplicate: CustomerDuplicateMatchDto | null;
}

export interface CustomerCreationRequestDto {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DUPLICATE';
  companyName: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  zipCode: string | null;
  city: string | null;
  website: string | null;
  notes: string | null;
  decisionNote: string | null;
  weclappPartyId: string | null;
  customerId: string | null;
  customerNumber: string | null;
  requestedByName: string | null;
  decidedByName: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export type ApproveCustomerRequestResult =
  | {
      outcome: 'created';
      success: true;
      customerId: string;
      /** null = im ServiceHub angelegt, weclapp folgt über die Warteliste. */
      weclappPartyId: string | null;
      customerNumber: string | null;
      weclappPending: boolean;
      weclappError: string | null;
    }
  | { outcome: 'duplicate'; match: CustomerDuplicateMatchDto };

export function createCustomerRequestsApi(request: RequestFn) {
  return {
    async createCustomerRequest(data: CustomerCreationRequestInput) {
      return request<{ request: { id: string; status: string } }>('/customer-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /** KIAN sucht Anschrift, Telefon, Mail, Webseite im Web — nur ein Vorschlag, gespeichert wird nichts. */
    async researchCustomerProfile(hints: CustomerProfileResearchHints) {
      return request<CustomerProfileResearchResult>('/customer-requests/research', {
        method: 'POST',
        body: JSON.stringify(hints),
      });
    },

    async getCustomerRequests() {
      return request<{ requests: CustomerCreationRequestDto[] }>('/customer-requests');
    },

    async approveCustomerRequest(id: string, opts?: { ignoreDuplicate?: boolean }) {
      return request<ApproveCustomerRequestResult>(
        `/customer-requests/${encodeURIComponent(id)}/approve`,
        { method: 'POST', body: JSON.stringify({ ignoreDuplicate: opts?.ignoreDuplicate === true }) }
      );
    },

    async acknowledgeCustomerRequestDuplicate(id: string) {
      return request<{ success: true; match: CustomerDuplicateMatchDto }>(
        `/customer-requests/${encodeURIComponent(id)}/acknowledge-duplicate`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    },

    async scanCustomerRequestDuplicates() {
      return request<{ scanned: number; resolved: number }>('/customer-requests/duplicate-scan', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async rejectCustomerRequest(id: string, reason: string) {
      return request<{ success: true }>(`/customer-requests/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },
  };
}
